import { api } from "@/lib/api";
import type { AuthUser } from "@/lib/auth";

export type MeUserResponse = { user: AuthUser };

export type OtpSendResult = {
  ok: boolean;
  email: string;
  message: string;
  code_sent_recently: boolean;
  cooldown_seconds: number;
};

export type EmailChangeSession = {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  user: AuthUser;
};

export async function updateDisplayName(display_name: string) {
  return api.fetch<MeUserResponse>("/auth/me", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ display_name }),
  });
}

export async function uploadAvatar(blob: Blob) {
  const formData = new FormData();
  formData.append("avatar", blob, blob.type === "image/png" ? "avatar.png" : "avatar.jpg");
  return api.fetch<MeUserResponse>("/auth/me/avatar", {
    method: "POST",
    body: formData,
  });
}

export async function deleteAvatar() {
  return api.fetch<MeUserResponse>("/auth/me/avatar", { method: "DELETE" });
}

export async function sendCurrentEmailOtp(options?: { force?: boolean }) {
  return api.fetch<OtpSendResult>("/auth/me/email/send_current_otp", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ force: options?.force === true }),
  });
}

export async function verifyCurrentEmail(code: string) {
  return api.fetch<{ ok: boolean }>("/auth/me/email/verify_current", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
}

export async function sendNewEmailOtp(email: string, options?: { force?: boolean }) {
  return api.fetch<OtpSendResult>("/auth/me/email/send_new_otp", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, force: options?.force === true }),
  });
}

export async function confirmNewEmail(email: string, code: string) {
  return api.fetch<EmailChangeSession>("/auth/me/email/confirm_new", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  });
}

export type MagicLinkResult = {
  url: string;
  expires_at: string;
};

export async function createMagicLink() {
  return api.fetch<MagicLinkResult>("/auth/me/magic_link", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
}

export type MeListMeta = { page: number; per_page: number; total: number };

export type LoginEvent = {
  id: number;
  occurred_at: string;
  method: string;
  result: "success" | "failure";
  ip: string | null;
  location: string | null;
  user_agent: string | null;
  failure_reason: string | null;
};

export type ActivityEvent = {
  id: number;
  created_at: string;
  source: string;
  action: string;
  summary: string;
  meta: Record<string, unknown>;
};

function appendListParams(
  sp: URLSearchParams,
  params?: { page?: number; per_page?: number; source?: "main_app" | "other_app" },
) {
  if (params?.page != null) sp.set("page", String(params.page));
  if (params?.per_page != null) sp.set("per_page", String(params.per_page));
  if (params?.source) sp.set("source", params.source);
}

export async function fetchLoginEvents(params?: { page?: number; per_page?: number }) {
  const sp = new URLSearchParams();
  appendListParams(sp, params);
  const qs = sp.toString();
  return api.fetch<{ login_events: LoginEvent[]; meta: MeListMeta }>(
    `/auth/me/login_events${qs ? `?${qs}` : ""}`,
  );
}

export async function fetchActivityEvents(params?: {
  page?: number;
  per_page?: number;
  source?: "main_app" | "other_app";
}) {
  const sp = new URLSearchParams();
  appendListParams(sp, params);
  const qs = sp.toString();
  return api.fetch<{ activity_events: ActivityEvent[]; meta: MeListMeta }>(
    `/auth/me/activity_events${qs ? `?${qs}` : ""}`,
  );
}
